/**
 * File Uploader
 * Handles file uploads with retry logic and base64 fallback
 */

import { API } from '../utils/api.js';

export class FileUploader {
  /**
   * Upload file with retry and base64 fallback
   * @param {File} file - File to upload
   * @param {string} fieldName - Field name
   * @param {number} attempt - Current attempt number
   * @param {boolean} useBase64 - Whether to use base64 encoding
   * @returns {Promise<Object>}
   */
  static async uploadFile(file, fieldName, attempt = 1, useBase64 = false) {
    const maxRetries = 3;
    
    try {
      if (useBase64) {
        return await this.uploadBase64(file, fieldName);
      } else {
        return await this.uploadMultipart(file, fieldName, attempt);
      }
    } catch (error) {
      if (attempt < maxRetries) {
        if (error.status === 403 && attempt === 1) {
          return this.uploadFile(file, fieldName, 1, true);
        } else {
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          return this.uploadFile(file, fieldName, attempt + 1, false);
        }
      }
      throw error;
    }
  }

  /**
   * @param {Object} response
   * @returns {Object}
   */
  static assertUploadOk(response) {
    if (!response || response.status === 'fail' || response.status === 'error' || response.error) {
      const err = new Error(response && (response.message || response.error) ? (response.message || response.error) : 'Upload failed');
      err.status = response && response.http_status ? response.http_status : 400;
      throw err;
    }
    return response;
  }

  /**
   * Upload file as multipart
   * @param {File} file - File to upload
   * @param {string} fieldName - Field name
   * @param {number} attempt - Attempt number
   * @returns {Promise<Object>}
   */
  static async uploadMultipart(file, fieldName, attempt) {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await API.uploadFile(formData);
    return this.assertUploadOk(response);
  }

  /**
   * Upload file as base64
   * @param {File} file - File to upload
   * @param {string} fieldName - Field name
   * @returns {Promise<Object>}
   */
  static async uploadBase64(file, fieldName) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result.split(',')[1];
          const response = await API.post('/tqtag/api/secure_file_upload_base64', {
            filename: file.name,
            file_data: base64Data
          });
          resolve(this.assertUploadOk(response));
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Upload multiple files with staggered delays
   * @param {Array} files - Array of files
   * @param {string} fieldName - Field name
   * @returns {Promise<Array>}
   */
  static async uploadFiles(files, fieldName) {
    const uploadPromises = [];
    
    files.forEach((file, index) => {
      const promise = new Promise(resolve => {
        setTimeout(async () => {
          try {
            const result = await this.uploadFile(file, fieldName);
            resolve(result);
          } catch (error) {
            resolve({ error: error.message });
          }
        }, index * 1000);
      });
      
      uploadPromises.push(promise);
    });
    
    return Promise.all(uploadPromises);
  }
}
